<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRolesStore } from '@/stores/roles'
import { useGroupsStore } from '@/stores/groups'
import { AgreementModal, AppUpdateRequired } from '@/components'
import ConfirmDialog from '@/components/ui/ConfirmDialog/ConfirmDialog.vue'
import { shouldShowAgreementModal } from '@/composables/useFacility'

const authStore = useAuthStore()
const rolesStore = useRolesStore()
const groupsStore = useGroupsStore()

// Not simply "agreement unsigned": gen1 prompted only those who could act on it — see
// shouldShowAgreementModal. Prompting anyone else strands them behind a modal whose only
// button is Logout.
const showAgreementModal = computed(() => {
  return authStore.isAuthenticated
    && shouldShowAgreementModal(authStore.user, authStore.user?.facility)
})

onMounted(async () => {
  // Try to fetch user on app load
  if (!authStore.isAuthenticated) {
    await authStore.fetchUser()
  }
  
  // If user is authenticated, fetch roles and groups
  if (authStore.isAuthenticated) {
    try {
      await Promise.all([
        rolesStore.fetchRoles(),
        groupsStore.fetchGroups()
      ])
    } catch (error) {
      console.error('Failed to fetch roles/groups on app load:', error)
    }
  }
})
</script>

<template>
  <router-view />
  <AgreementModal v-if="showAgreementModal" />
  <ConfirmDialog />
  <AppUpdateRequired />
</template>

<style>
/* App-level styles - Using Global Theme System */
html {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden !important;
  background-color: var(--color-background);
}

body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow-x: hidden;
  background-color: var(--color-background);
}

#app {
  height: 100%;
  background-color: var(--color-background);
}

/* Remove custom font-family - let global theme handle it */
.v-application {
  background-color: var(--color-background) !important;
}

/* Typography improvements using design tokens */
h1, h2, h3, h4, h5, h6 {
  color: var(--color-on-surface);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.025em;
}
</style>
